/**
 * Water Detection Utility
 * Analyzes map images to detect water content based on blue color ranges
 */

/**
 * Defines the range of blue colors that represent water in maps
 * Updated to better match typical Mapbox water colors and the blue shown in the user's image
 */
const WATER_COLOR_RANGES = [
    // Light blue water (typical for shallow/coastal water) - RGB values like in the user's image
    { r: [150, 180], g: [210, 230], b: [240, 255] },
    // Medium blue water (rivers, lakes) - similar to user's image blue
    { r: [120, 170], g: [190, 220], b: [220, 255] },
    // Standard Mapbox water color (deeper blue)
    { r: [100, 160], g: [170, 210], b: [200, 250] },
    // Darker blue water (very deep water/ocean)
    { r: [80, 140], g: [140, 190], b: [180, 230] },
    // Very light blue (almost white-blue water in bright areas)
    { r: [180, 210], g: [220, 245], b: [245, 255] },
    // Specific color range matching the user's image sample (RGB: 167, 218, 250)
    { r: [160, 175], g: [210, 225], b: [245, 255] },
];

/**
 * Enhanced pixel classification that also considers color relationships
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)  
 * @param {number} b - Blue value (0-255)
 * @returns {boolean} - True if the pixel represents water
 */
function isWaterPixel(r, g, b) {
    // First check against defined color ranges
    const matchesRange = WATER_COLOR_RANGES.some(range => 
        r >= range.r[0] && r <= range.r[1] &&
        g >= range.g[0] && g <= range.g[1] &&
        b >= range.b[0] && b <= range.b[1]
    );
    
    if (matchesRange) return true;
    
    // Additional heuristic: check if it's a blue-ish color typical of water
    // Water typically has: blue > green > red and high blue saturation
    const isBlueish = b > g && b > r && b > 180;
    const hasWaterCharacteristics = g > r && (b - r) > 40 && (b - g) > 20;
    
    return isBlueish && hasWaterCharacteristics;
}

/**
 * Analyzes an image element to detect water percentage
 * @param {HTMLImageElement} imageElement - The image to analyze
 * @returns {Promise<{waterPercentage: number, totalPixels: number, waterPixels: number}>}
 */
export function analyzeImageWaterContent(imageElement) {
    return new Promise((resolve, reject) => {
        try {
            if (localStorage.getItem('waterDetectionDebug') === 'true') {
                console.log(`[WaterDetection] Starting pixel analysis for image: ${imageElement.src ? imageElement.src.substring(0, 100) : 'Embedded Image'}...`);
            }
            // Create canvas to read pixel data
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Set canvas size to match image
            canvas.width = imageElement.naturalWidth || imageElement.width;
            canvas.height = imageElement.naturalHeight || imageElement.height;

            // Draw image to canvas
            ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

            // Get pixel data
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const pixels = imageData.data;

            let waterPixels = 0;
            const totalPixels = pixels.length / 4; // 4 values per pixel (RGBA)

            // Analyze every pixel (or sample for performance on large images)
            const sampleRate = totalPixels > 100000 ? 4 : 1; // Sample every 4th pixel if image is large

            for (let i = 0; i < pixels.length; i += 4 * sampleRate) {
                const r = pixels[i];
                const g = pixels[i + 1];
                const b = pixels[i + 2];
                const a = pixels[i + 3];

                // Skip transparent pixels
                if (a < 128) continue;

                if (isWaterPixel(r, g, b)) {
                    waterPixels++;
                }
            }

            const sampledTotalPixels = Math.floor(totalPixels / sampleRate);
            const waterPercentage = sampledTotalPixels > 0 ? (waterPixels / sampledTotalPixels) * 100 : 0;

            resolve({
                waterPercentage: Math.round(waterPercentage * 100) / 100, // Round to 2 decimal places
                totalPixels: sampledTotalPixels,
                waterPixels
            });

        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Analyzes a canvas element to detect water percentage
 * @param {HTMLCanvasElement} canvasElement - The canvas to analyze
 * @returns {Promise<{waterPercentage: number, totalPixels: number, waterPixels: number}>}
 */
export function analyzeCanvasWaterContent(canvasElement) {
    return new Promise((resolve, reject) => {
        try {
            if (localStorage.getItem('waterDetectionDebug') === 'true') {
                console.log(`[WaterDetection] Starting pixel analysis for canvas: ${canvasElement.width}x${canvasElement.height}`);
            }
            const ctx = canvasElement.getContext('2d');
            const imageData = ctx.getImageData(0, 0, canvasElement.width, canvasElement.height);
            const pixels = imageData.data;

            let waterPixels = 0;
            const totalPixels = pixels.length / 4;

            // Sample for performance on large canvases
            const sampleRate = totalPixels > 100000 ? 4 : 1;

            for (let i = 0; i < pixels.length; i += 4 * sampleRate) {
                const r = pixels[i];
                const g = pixels[i + 1];
                const b = pixels[i + 2];
                const a = pixels[i + 3];

                // Skip transparent pixels
                if (a < 128) continue;

                if (isWaterPixel(r, g, b)) {
                    waterPixels++;
                }
            }

            const sampledTotalPixels = Math.floor(totalPixels / sampleRate);
            const waterPercentage = sampledTotalPixels > 0 ? (waterPixels / sampledTotalPixels) * 100 : 0;

            resolve({
                waterPercentage: Math.round(waterPercentage * 100) / 100,
                totalPixels: sampledTotalPixels,
                waterPixels
            });

        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Analyzes a Mapbox map instance to detect water percentage
 * @param {mapboxgl.Map} map - The Mapbox map instance
 * @returns {Promise<{waterPercentage: number, totalPixels: number, waterPixels: number}>}
 */
export function analyzeMapboxWaterContent(map) {
    return new Promise((resolve, reject) => {
        try {
            // Wait for map to be fully loaded and rendered
            if (!map.loaded()) {
                map.once('idle', () => {
                    analyzeMapboxWaterContent(map).then(resolve).catch(reject);
                });
                return;
            }

            // Get the canvas from the Mapbox map
            const canvas = map.getCanvas();
            analyzeCanvasWaterContent(canvas).then(resolve).catch(reject);

        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Analyzes water content from a static map URL
 * @param {string} imageUrl - URL of the static map image
 * @returns {Promise<{waterPercentage: number, totalPixels: number, waterPixels: number}>}
 */
export function analyzeStaticMapWaterContent(imageUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Enable CORS for analysis

        img.onload = () => {
            analyzeImageWaterContent(img).then(resolve).catch(reject);
        };

        img.onerror = () => {
            reject(new Error('Failed to load image for water analysis'));
        };

        img.src = imageUrl;
    });
}

/**
 * Determines the appropriate style categories based on water percentage
 * @param {number} waterPercentage - Percentage of water in the map (0-100)
 * @returns {Object} - Object containing available style categories
 */
export function determineAvailableStyles(waterPercentage) {
    const categories = {
        land: true,  // Always available
        coastal: false,
        ocean: false,
        river: false
    };

    if (waterPercentage > 0.5) {
        categories.river = true;
    }

    if (waterPercentage > 5) {
        categories.coastal = true;
    }

    if (waterPercentage > 20) {
        categories.ocean = true;
    }

    return {
        categories,
        waterPercentage,
        recommendation: getStyleRecommendation(waterPercentage)
    };
}

/**
 * Gets a style recommendation based on water percentage
 * @param {number} waterPercentage - Percentage of water in the map
 * @returns {string} - Recommended style category
 */
function getStyleRecommendation(waterPercentage) {
    if (waterPercentage > 20) return 'ocean';
    if (waterPercentage > 5) return 'coastal';
    if (waterPercentage > 0.5) return 'river';
    return 'land';
}

/**
 * Main function to analyze an activity's map and determine available styles
 * @param {Object} activity - Strava activity object
 * @param {HTMLElement|string} mapSource - Map element or static map URL
 * @returns {Promise<Object>} - Analysis results with style recommendations
 */
export async function analyzeActivityWaterContent(activity, mapSource) {
    try {
        let analysisResult;

        if (typeof mapSource === 'string') {
            // Static map URL
            analysisResult = await analyzeStaticMapWaterContent(mapSource);
        } else if (mapSource instanceof HTMLImageElement) {
            // Image element
            analysisResult = await analyzeImageWaterContent(mapSource);
        } else if (mapSource instanceof HTMLCanvasElement) {
            // Canvas element
            analysisResult = await analyzeCanvasWaterContent(mapSource);
        } else if (mapSource && typeof mapSource.getCanvas === 'function') {
            // Mapbox map instance
            analysisResult = await analyzeMapboxWaterContent(mapSource);
        } else {
            throw new Error('Unsupported map source type');
        }

        const styleRecommendations = determineAvailableStyles(analysisResult.waterPercentage);

        return {
            ...analysisResult,
            ...styleRecommendations,
            activity: {
                id: activity.id,
                name: activity.name
            }
        };

    } catch (error) {
        console.error('Water content analysis failed:', error);
        // Return default result if analysis fails
        return {
            waterPercentage: 0,
            totalPixels: 0,
            waterPixels: 0,
            categories: { land: true, coastal: false, ocean: false, river: false },
            recommendation: 'land',
            error: error.message
        };
    }
} 
